import { createSlice } from "@reduxjs/toolkit";
import {
  getAllVehiclesByCaseIdAction,
  getVehicleByIdAction,
  createVehicleAction,
  updateVehicleAction,
  deleteVehicleAction,
  searchVehiclesAction,
  bulkCreateVehiclesAction,
  getVehiclesWithPaginationAction,
  exportVehiclesToExcelAction,
  clearVehicleStateAction,
  setSelectedVehicleAction,
  setVehicleFiltersAction
} from "../../actions/Case/caseVehicleDetailsAssetAction";

const initialState = {
  vehicles: [],
  assetOwned:{},
  selectedVehicle: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  },
  loading: false,
  error: null,
  success: false,
  message: "",
  exportLoading: false,
  exportError: null,
  exportData: null
};

const vehicleSlice = createSlice({
  name: "vehicle",
  initialState,
  reducers: {
    // You can add synchronous reducers here if needed
    resetVehicleState: (state) => {
      return initialState;
    },
    clearVehicleError: (state) => {
      state.error = null;
      state.message = "";
    },
    updateVehicleInList: (state, action) => {
      const index = state.vehicles.findIndex(
        v => v.vehicleID === action.payload.vehicleID
      );
      if (index !== -1) {
        state.vehicles[index] = { ...state.vehicles[index], ...action.payload };
      }
    },
    removeVehicleFromList: (state, action) => {
      state.vehicles = state.vehicles.filter(
        v => v.vehicleID !== action.payload
      );
    },
    // ADD THIS: Clear selected vehicle action
    clearSelectedVehicle: (state) => {
      state.selectedVehicle = null;
    }
  },
  extraReducers: (builder) => {
    // =========== GET ALL VEHICLES BY CASE ID ===========
    builder
      .addCase(getAllVehiclesByCaseIdAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAllVehiclesByCaseIdAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.vehicles = action.payload.vehicles;
        state.assetOwned=action.payload.assetOwnedBy;
        state.message = action.payload.message;
      })
      .addCase(getAllVehiclesByCaseIdAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch vehicles";
        state.vehicles = [];
      })

    // =========== GET SINGLE VEHICLE BY ID ===========
    builder
      .addCase(getVehicleByIdAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getVehicleByIdAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.selectedVehicle = action.payload.vehicle;
        state.message = action.payload.message;
      })
      .addCase(getVehicleByIdAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch vehicle";
        state.selectedVehicle = null;
      })

    // =========== CREATE NEW VEHICLE ===========
    builder
      .addCase(createVehicleAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createVehicleAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.vehicles.unshift(action.payload.vehicle); // Add to beginning
        state.message = action.payload.message;
      })
      .addCase(createVehicleAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create vehicle";
        if (action.payload?.errors) {
          state.validationErrors = action.payload.errors;
        }
      })

    // =========== UPDATE VEHICLE ===========
    builder
      .addCase(updateVehicleAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateVehicleAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Update in vehicles list
        const index = state.vehicles.findIndex(
          v => v.vehicleID === action.payload.vehicleID
        );
        if (index !== -1) {
          state.vehicles[index] = action.payload.vehicle;
        }
        // Update selected vehicle if it's the same
        if (state.selectedVehicle?.vehicleID === action.payload.vehicleID) {
          state.selectedVehicle = action.payload.vehicle;
        }
        state.message = action.payload.message;
      })
      .addCase(updateVehicleAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update vehicle";
      })

    // =========== DELETE VEHICLE ===========
    builder
      .addCase(deleteVehicleAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteVehicleAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Remove from vehicles list
        state.vehicles = state.vehicles.filter(
          v => v.vehicleID !== action.payload.vehicleID
        );
        // Clear selected vehicle if it's the deleted one
        if (state.selectedVehicle?.vehicleID === action.payload.vehicleID) {
          state.selectedVehicle = null;
        }
        state.message = action.payload.message;
      })
      .addCase(deleteVehicleAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete vehicle";
      })

    // =========== SEARCH VEHICLES ===========
    builder
      .addCase(searchVehiclesAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(searchVehiclesAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.vehicles = action.payload.vehicles;
        state.filters = action.payload.searchCriteria;
        state.message = action.payload.message;
      })
      .addCase(searchVehiclesAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to search vehicles";
        state.vehicles = [];
      })

    // =========== BULK CREATE VEHICLES ===========
    builder
      .addCase(bulkCreateVehiclesAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(bulkCreateVehiclesAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Add all new vehicles to the list
        state.vehicles = [...action.payload.vehicles, ...state.vehicles];
        state.message = action.payload.message;
      })
      .addCase(bulkCreateVehiclesAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to bulk create vehicles";
      })

    // =========== GET VEHICLES WITH PAGINATION ===========
    builder
      .addCase(getVehiclesWithPaginationAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getVehiclesWithPaginationAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.vehicles = action.payload.vehicles;
        state.pagination = action.payload.pagination;
        state.message = action.payload.message;
      })
      .addCase(getVehiclesWithPaginationAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch vehicles";
      })

    // =========== EXPORT VEHICLES TO EXCEL ===========
    builder
      .addCase(exportVehiclesToExcelAction.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportVehiclesToExcelAction.fulfilled, (state, action) => {
        state.exportLoading = false;
        state.exportData = action.payload.blobData;
        state.message = action.payload.message;
      })
      .addCase(exportVehiclesToExcelAction.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload?.message || "Failed to export vehicles";
      })

    // =========== For regular actions (non-thunks), use addMatcher or handle them in reducers ===========
    // Alternative 1: Move them to the reducers section above
    // Alternative 2: Use builder.addMatcher if you need to check the action type
    builder
      .addMatcher(
        (action) => action.type === clearVehicleStateAction.type,
        () => initialState
      )
      .addMatcher(
        (action) => action.type === setSelectedVehicleAction.type,
        (state, action) => {
          state.selectedVehicle = action.payload;
        }
      )
      .addMatcher(
        (action) => action.type === setVehicleFiltersAction.type,
        (state, action) => {
          state.filters = action.payload;
        }
      );
  }
});

// ADD clearSelectedVehicle to the exported actions
export const {
  resetVehicleState,
  clearVehicleError,
  updateVehicleInList,
  removeVehicleFromList,
  clearSelectedVehicle  // Add this line
} = vehicleSlice.actions;

export default vehicleSlice.reducer;